import { PDFDocument, rgb, StandardFonts, degrees } from 'pdf-lib';
import axiosInstance from 'Services/APIService';
import { AssignedDocument, InputField } from '../types';

interface DownloadUtilsProps {
  showMessage: (message: string, isError?: boolean) => void;
  setDownloadLoading: (key: string | null) => void;
}

// ✅ CRITICAL FIX: Precise coordinate conversion that matches your overlay positioning
const convertOverlayToPdfCoordinates = (
  field: InputField,
  page: any,
  containerRef?: React.RefObject<HTMLDivElement>
) => {
  const { width: pdfWidth, height: pdfHeight } = page.getSize();

  console.log(`📐 PDF Page Size: ${pdfWidth} x ${pdfHeight}`);
  console.log(
    `🎯 Field Coordinates: x=${field.x}, y=${field.y}, xP=${field.xPercent}, yP=${field.yPercent}`
  );

  let pdfX: number, pdfY: number;

  // Method 1: Use percentage coordinates if available (most reliable for your case)
  if (field.xPercent !== undefined && field.yPercent !== undefined) {
    pdfX = (field.xPercent / 100) * pdfWidth;
    pdfY = pdfHeight - (field.yPercent / 100) * pdfHeight; // Flip Y coordinate
    console.log(`📊 Using percentage coordinates: ${field.xPercent}%, ${field.yPercent}%`);
  } else {
    // Method 2: Use pixel coordinates with proper scaling
    if (containerRef?.current) {
      const documentElement = containerRef.current.querySelector('canvas, img');

      if (documentElement) {
        const elementWidth = documentElement.clientWidth;
        const elementHeight = documentElement.clientHeight;

        // Calculate scale factors based on actual rendered size
        const scaleX = pdfWidth / elementWidth;
        const scaleY = pdfHeight / elementHeight;

        pdfX = (field.x || 0) * scaleX;
        pdfY = pdfHeight - (field.y || 0) * scaleY; // Flip Y coordinate

        console.log(`📏 Dynamic scaling: scaleX=${scaleX.toFixed(3)}, scaleY=${scaleY.toFixed(3)}`);
        console.log(`📏 Container size: ${elementWidth}x${elementHeight}`);
      } else {
        // Fallback to A4 scaling
        const A4_WIDTH = 595;
        const A4_HEIGHT = 842;
        const scaleX = pdfWidth / A4_WIDTH;
        const scaleY = pdfHeight / A4_HEIGHT;

        pdfX = (field.x || 0) * scaleX;
        pdfY = pdfHeight - (field.y || 0) * scaleY;

        console.log(`📏 A4 fallback scaling: ${scaleX.toFixed(3)}, ${scaleY.toFixed(3)}`);
      }
    } else {
      // Final fallback to standard A4 scaling
      const A4_WIDTH = 595;
      const A4_HEIGHT = 842;
      const scaleX = pdfWidth / A4_WIDTH;
      const scaleY = pdfHeight / A4_HEIGHT;

      pdfX = (field.x || 0) * scaleX;
      pdfY = pdfHeight - (field.y || 0) * scaleY;

      console.log(`📏 Standard A4 scaling: ${scaleX.toFixed(3)}, ${scaleY.toFixed(3)}`);
    }
  }

  // Ensure coordinates are within bounds
  pdfX = Math.max(0, Math.min(pdfX, pdfWidth - 10));
  pdfY = Math.max(0, Math.min(pdfY, pdfHeight - 10));

  console.log(`✨ Final PDF Coordinates: (${pdfX.toFixed(2)}, ${pdfY.toFixed(2)})`);
  return { pdfX, pdfY };
};

// ✅ ENHANCED: Text drawing with precise positioning and high visibility
const handleTextField = async (page: any, field: InputField, x: number, y: number, font: any) => {
  const fieldValue = field.value?.toString()?.trim() || '';
  if (!fieldValue) return;

  // Use large, highly visible font
  const fontSize = Math.max(14, Math.min(20, fieldValue.length > 10 ? 12 : 16));

  // Position text slightly above the coordinate for better visibility
  const textX = x + 3;
  const textY = y + 8; // Adjusted positioning

  console.log(`✏️ Drawing text "${fieldValue}" at (${textX}, ${textY}) with font size ${fontSize}`);

  try {
    // Calculate text dimensions for background
    const textWidth = font.widthOfTextAtSize(fieldValue, fontSize);
    const padding = 4;

    // Draw bright background rectangle
    page.drawRectangle({
      x: Math.max(0, textX - padding),
      y: Math.max(0, textY - padding),
      width: Math.min(textWidth + padding * 2, page.getWidth() - textX + padding),
      height: fontSize + padding * 2,
      color: rgb(1, 1, 0.7), // Bright yellow background
      opacity: 0.9
    });

    // Draw border for extra visibility
    page.drawRectangle({
      x: Math.max(0, textX - padding),
      y: Math.max(0, textY - padding),
      width: Math.min(textWidth + padding * 2, page.getWidth() - textX + padding),
      height: fontSize + padding * 2,
      borderColor: rgb(0.2, 0.2, 0.2),
      borderWidth: 1
    });

    // Draw the text
    page.drawText(fieldValue, {
      x: textX,
      y: textY,
      size: fontSize,
      font: font,
      color: rgb(0, 0, 0), // Black text for maximum contrast
      maxWidth: Math.max(100, page.getWidth() - textX - 20)
    });

    console.log(`✅ Successfully drew text field`);
  } catch (error) {
    console.error(`❌ Text drawing failed:`, error);

    // Fallback: simple text without background
    try {
      page.drawText(fieldValue, {
        x: textX,
        y: textY,
        size: 14,
        font: font,
        color: rgb(1, 0, 0) // Red fallback for visibility
      });
      console.log(`⚠️ Fallback text drawing successful`);
    } catch (fallbackError) {
      console.error(`❌ Fallback text drawing failed:`, fallbackError);
    }
  }
};

// ✅ ENHANCED: Signature drawing with better error handling and positioning
const handleSignatureField = async (
  pdfDoc: any,
  page: any,
  field: InputField,
  x: number,
  y: number,
  font: any
) => {
  const signatureValue = field.value?.toString() || '';
  if (!signatureValue) return;

  console.log(`✍️ Drawing signature at (${x}, ${y})`);

  try {
    if (signatureValue.startsWith('data:image/')) {
      // Handle image signature (from signature pad)
      const response = await fetch(signatureValue);
      const imageBytes = await response.arrayBuffer();

      let image;
      try {
        image = await pdfDoc.embedPng(imageBytes);
      } catch {
        try {
          image = await pdfDoc.embedJpg(imageBytes);
        } catch (embedError) {
          console.error('❌ Failed to embed signature image:', embedError);
          // Fallback to text indicator
          page.drawText('[Signature]', {
            x: x + 5,
            y: y + 5,
            size: 12,
            font: font,
            color: rgb(0, 0, 0.8)
          });
          return;
        }
      }

      const sigWidth = Math.min(150, page.getWidth() - x - 10);
      const sigHeight = Math.min(60, page.getHeight() - y - 10);

      // Draw signature background
      page.drawRectangle({
        x: x - 2,
        y: y - 2,
        width: sigWidth + 4,
        height: sigHeight + 4,
        color: rgb(0.95, 0.95, 1),
        opacity: 0.8,
        borderColor: rgb(0.5, 0.5, 0.8),
        borderWidth: 1
      });

      // Draw signature image
      page.drawImage(image, {
        x: x,
        y: y,
        width: sigWidth,
        height: sigHeight
      });

      console.log(`✅ Drew signature image (${sigWidth}x${sigHeight})`);
    } else if (signatureValue === 'checked') {
      // Handle checkbox
      const checkSize = 18;

      // Draw checkbox background
      page.drawRectangle({
        x: x,
        y: y,
        width: checkSize + 4,
        height: checkSize + 4,
        color: rgb(0.9, 1, 0.9),
        borderColor: rgb(0, 0.7, 0),
        borderWidth: 1
      });

      // Draw checkmark
      page.drawText('✓', {
        x: x + 2,
        y: y + 2,
        size: checkSize,
        font: font,
        color: rgb(0, 0.8, 0)
      });

      console.log(`✅ Drew checkbox`);
    } else {
      // Handle typed signature
      const fontSize = 16;
      const textWidth = font.widthOfTextAtSize(signatureValue, fontSize);

      // Draw signature background
      page.drawRectangle({
        x: x - 2,
        y: y - 2,
        width: Math.min(textWidth + 8, page.getWidth() - x + 2),
        height: fontSize + 8,
        color: rgb(0.95, 0.95, 1),
        opacity: 0.8,
        borderColor: rgb(0.5, 0.5, 0.8),
        borderWidth: 1
      });

      // Draw signature text with italic font
      try {
        const italicFont = await pdfDoc.embedFont(StandardFonts.HelveticaOblique);
        page.drawText(signatureValue, {
          x: x + 2,
          y: y + 2,
          size: fontSize,
          font: italicFont,
          color: rgb(0, 0, 0.8)
        });
      } catch {
        page.drawText(signatureValue, {
          x: x + 2,
          y: y + 2,
          size: fontSize,
          font: font,
          color: rgb(0, 0, 0.8)
        });
      }

      console.log(`✅ Drew typed signature`);
    }
  } catch (error) {
    console.error(`❌ Signature drawing failed:`, error);
  }
};

// ✅ MAIN FUNCTION: Enhanced with better coordinate mapping and debugging
export const handleDownloadFilledDocument = async (
  assignedDoc: AssignedDocument,
  { showMessage, setDownloadLoading }: DownloadUtilsProps,
  pdfContainerRef?: React.RefObject<HTMLDivElement>
) => {
  console.log(`🚀 === PDF GENERATION WITH CORRECT POSITIONING START ===`);
  console.log(`📄 Document: ${assignedDoc.documentName}`);
  console.log(`🏷️ Fields to process: ${assignedDoc.inputFields?.length || 0}`);

  if (!assignedDoc) {
    showMessage('No document to download', true);
    return;
  }

  const downloadKey = `download-filled-${assignedDoc.documentId}`;
  setDownloadLoading(downloadKey);

  try {
    let originalPdfBytes: ArrayBuffer;

    // Handle different file sources
    if (assignedDoc.uploadedFile?.storedFilePath) {
      console.log(`📥 Loading PDF from: ${assignedDoc.uploadedFile.storedFilePath}`);

      try {
        const response = await axiosInstance.get(
          `/avatar/get-avatar?path=${encodeURIComponent(assignedDoc.uploadedFile.storedFilePath)}`,
          {
            responseType: 'arraybuffer',
            timeout: 30000
          }
        );
        originalPdfBytes = response.data;
        console.log(`✅ PDF loaded: ${originalPdfBytes.byteLength} bytes`);
      } catch (networkError: any) {
        console.error(`❌ Network error loading PDF:`, networkError);
        showMessage('Failed to load PDF. Please check your connection and try again.', true);
        return;
      }
    } else {
      showMessage('No PDF file path available for processing', true);
      return;
    }

    // Validate PDF data
    if (!originalPdfBytes || originalPdfBytes.byteLength === 0) {
      showMessage('Invalid PDF data received', true);
      return;
    }

    const pdfDoc = await PDFDocument.load(originalPdfBytes);
    const pages = pdfDoc.getPages();
    const font = await pdfDoc.embedFont(StandardFonts.Helvetica);

    console.log(`📑 PDF has ${pages.length} pages`);

    if (assignedDoc.inputFields && assignedDoc.inputFields.length > 0) {
      let processedCount = 0;
      let errorCount = 0;

      // Group fields by page for better processing
      const fieldsByPage = assignedDoc.inputFields.reduce((acc, field) => {
        if (field.value && field.value.toString().trim() !== '') {
          const pageNum = field.pageNumber || 1;
          if (!acc[pageNum]) acc[pageNum] = [];
          acc[pageNum].push(field);
        }
        return acc;
      }, {} as Record<number, InputField[]>);

      console.log(`📊 Fields grouped by pages:`, Object.keys(fieldsByPage));

      for (const [pageNumStr, pageFields] of Object.entries(fieldsByPage)) {
        const pageNum = parseInt(pageNumStr);
        const pageIndex = pageNum - 1;

        if (pageIndex < 0 || pageIndex >= pages.length) {
          console.warn(`⚠️ Page ${pageNum} out of range, skipping ${pageFields.length} fields`);
          continue;
        }

        const page = pages[pageIndex];
        console.log(`\n📄 Processing Page ${pageNum} with ${pageFields.length} fields`);

        for (const field of pageFields) {
          processedCount++;

          console.log(
            `\n🔄 [${processedCount}] Processing "${field.type}" with value "${field.value}"`
          );

          try {
            // Convert coordinates with container reference for accurate scaling
            const { pdfX, pdfY } = convertOverlayToPdfCoordinates(field, page, pdfContainerRef);

            // Validate coordinates
            if (pdfX < 0 || pdfY < 0 || pdfX > page.getWidth() || pdfY > page.getHeight()) {
              console.warn(
                `⚠️ Coordinates potentially out of bounds: (${pdfX.toFixed(2)}, ${pdfY.toFixed(2)})`
              );
            }

            // Draw field based on type
            if (field.type.toLowerCase() === 'signature') {
              await handleSignatureField(pdfDoc, page, field, pdfX, pdfY, font);
            } else {
              await handleTextField(page, field, pdfX, pdfY, font);
            }

            console.log(`✅ Successfully processed field ${processedCount}`);
          } catch (fieldError) {
            console.error(`❌ Error processing field "${field.type}":`, fieldError);
            errorCount++;
          }
        }

        console.log(`✅ Page ${pageNum} completed: ${pageFields.length} fields processed`);
      }

      console.log(`\n📊 Processing Summary:`);
      console.log(`✅ Successfully processed: ${processedCount - errorCount} fields`);
      console.log(`❌ Failed to process: ${errorCount} fields`);
      console.log(`📄 Pages processed: ${Object.keys(fieldsByPage).length}`);

      if (errorCount > 0) {
        showMessage(
          `PDF generated with some issues: ${errorCount} fields could not be processed`,
          false
        );
      }
    } else {
      console.warn(`⚠️ No input fields found to process`);
    }

    // Generate and download PDF
    // Generate and download PDF
    console.log(`📤 Generating final PDF with positioned tags...`);
    // ✅ Create a new Uint8Array with explicit ArrayBuffer typing
    const filledPdfBytes = await pdfDoc.save();
    const uint8Array = new Uint8Array(filledPdfBytes); // This creates ArrayBuffer-based Uint8Array
    const blob = new Blob([uint8Array], { type: 'application/pdf' });

    const url = URL.createObjectURL(blob);

    // Create and trigger download
    const link = window.document.createElement('a');
    link.href = url;
    link.download = `${assignedDoc.documentName}_${assignedDoc.assignedEmpName}_filled.pdf`;
    link.style.display = 'none';

    window.document.body.appendChild(link);
    link.click();
    window.document.body.removeChild(link);

    // Clean up
    setTimeout(() => URL.revokeObjectURL(url), 1000);

    console.log(`🎉 === PDF GENERATION WITH POSITIONED TAGS COMPLETE ===`);
    showMessage(
      `Filled document "${assignedDoc.documentName}" downloaded with positioned tags successfully!`
    );
  } catch (error: any) {
    console.error('❌ PDF generation failed:', error);

    if (error.message?.includes('Invalid PDF')) {
      showMessage(
        'The PDF file appears to be corrupted. Please try with a different document.',
        true
      );
    } else if (error.message?.includes('network')) {
      showMessage('Network error occurred. Please check your connection and try again.', true);
    } else {
      showMessage(`Failed to generate PDF: ${error.message || 'Unknown error occurred'}`, true);
    }
  } finally {
    setDownloadLoading(null);
  }
};

// ✅ Original document download function
export const handleDownloadOriginalDocument = async (
  assignedDoc: AssignedDocument,
  { showMessage, setDownloadLoading }: DownloadUtilsProps
) => {
  if (!assignedDoc.uploadedFile?.storedFilePath) {
    showMessage('No file available for download', true);
    return;
  }

  const downloadKey = `download-original-${assignedDoc.documentId}`;
  setDownloadLoading(downloadKey);

  try {
    const response = await axiosInstance.get(
      `/avatar/get-avatar?path=${encodeURIComponent(assignedDoc.uploadedFile.storedFilePath)}`,
      { responseType: 'blob', timeout: 30000 }
    );

    const fileType = assignedDoc.uploadedFile.fileType || 'pdf';
    const blob = new Blob([response.data], { type: `application/${fileType}` });
    const url = URL.createObjectURL(blob);

    const link = window.document.createElement('a');
    link.href = url;
    link.download = `${assignedDoc.documentName}_${assignedDoc.assignedEmpName}_original.${fileType}`;
    link.style.display = 'none';

    window.document.body.appendChild(link);
    link.click();
    window.document.body.removeChild(link);

    setTimeout(() => URL.revokeObjectURL(url), 1000);

    showMessage(`Original document "${assignedDoc.documentName}" downloaded successfully!`);
  } catch (error: any) {
    console.error('❌ Original document download error:', error);
    showMessage(`Failed to download document: ${error.message}`, true);
  } finally {
    setDownloadLoading(null);
  }
};

// ✅ Image document download function
export const handleDownloadImageDocument = async (
  assignedDoc: AssignedDocument,
  pdfContainerRef: React.RefObject<HTMLDivElement>,
  { showMessage, setDownloadLoading }: DownloadUtilsProps
) => {
  if (!pdfContainerRef.current) {
    showMessage('Document container not found', true);
    return;
  }

  const downloadKey = `download-image-${assignedDoc.documentId}`;
  setDownloadLoading(downloadKey);

  try {
    const hasFilledFields = assignedDoc.inputFields?.some(
      (field) => field.value && field.value.toString().trim() !== ''
    );

    if (hasFilledFields) {
      const html2canvas = (await import('html2canvas')).default;

      const canvas = await html2canvas(pdfContainerRef.current, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff'
      });

      canvas.toBlob(
        (blob) => {
          if (blob) {
            const url = URL.createObjectURL(blob);
            const link = window.document.createElement('a');
            link.href = url;
            link.download = `${assignedDoc.documentName}_${assignedDoc.assignedEmpName}_filled.png`;
            link.style.display = 'none';

            window.document.body.appendChild(link);
            link.click();
            window.document.body.removeChild(link);

            setTimeout(() => URL.revokeObjectURL(url), 1000);

            showMessage(`Filled image "${assignedDoc.documentName}" downloaded successfully!`);
          }
        },
        'image/png',
        0.95
      );
    } else {
      const storedFilePath = assignedDoc.uploadedFile?.storedFilePath;
      if (!storedFilePath) {
        showMessage('Document file path is missing', true);
        return;
      }

      const response = await axiosInstance.get(
        `/avatar/get-avatar?path=${encodeURIComponent(storedFilePath)}`,
        { responseType: 'blob', timeout: 30000 }
      );

      const fileType = assignedDoc.uploadedFile?.fileType || 'png';
      const blob = new Blob([response.data], { type: `image/${fileType}` });
      const url = URL.createObjectURL(blob);

      const link = window.document.createElement('a');
      link.href = url;
      link.download = `${assignedDoc.documentName}_${assignedDoc.assignedEmpName}.${fileType}`;
      link.style.display = 'none';

      window.document.body.appendChild(link);
      link.click();
      window.document.body.removeChild(link);

      setTimeout(() => URL.revokeObjectURL(url), 1000);

      showMessage(`Image "${assignedDoc.documentName}" downloaded successfully!`);
    }
  } catch (error: any) {
    console.error('❌ Image download error:', error);
    showMessage(`Failed to download image: ${error.message}`, true);
  } finally {
    setDownloadLoading(null);
  }
};
